import {Component, EventEmitter, Input, Output} from '@angular/core';
import {User} from '../models/user';
import {KvItem} from '../models/kv-item';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { CountryService } from 'app/modules/core/services/country';

@Component({
  selector: 'user-form',
  template: `
  <form (ngSubmit)="save()">
    <div class="row form-group">
      <div class="col-md-6" [ngClass]="{'has-error': usernameTaken}">
        <label>User Name (leave blank to use email)</label>
        <div *ngIf="usernameTaken" class="red small">A user with this user name already exists</div>
        <input class="form-control form-control-sm"
               [ngModel]="username"
               [formControl]="userForm.controls['userName']"
               (blur)="validateUsername()" />
      </div>
    </div>
    <div class="row form-group">
      <div class="col-md-9"
           [ngClass]="{'has-error': (!userForm.controls['email'].valid && userForm.controls['email'].touched) || emailTaken }">
        <label>Email Address *</label>
        <div *ngIf="emailTaken" class="red small">A user with this email address already exists</div>
        <input class="form-control form-control-sm"
               [ngModel]="email"
               [formControl]="userForm.controls['email']"
               (blur)="validateEmail()" />
      </div>
      <div class="col-md-3">
        <label class="check-box-field-align">
          <input type="checkbox"
                 [ngModel]="enabled"
                 [formControl]="userForm.controls['enabled']"/>
          Enabled
        </label>
      </div>
    </div>
    <div class="row form-group">
      <div class="col-md-4"
           [ngClass]="{'has-error':!userForm.controls['firstName'].valid && userForm.controls['firstName'].touched}">
        <label>First Name *</label>
        <input class="form-control form-control-sm"
               [ngModel]="firstName"
               [formControl]="userForm.controls['firstName']"/>
      </div>
      <div class="col-md-4"
           [ngClass]="{'has-error':!userForm.controls['lastName'].valid && userForm.controls['lastName'].touched}">
        <label>Last Name *</label>
        <input class="form-control form-control-sm"
               [ngModel]="lastName"
               [formControl]="userForm.controls['lastName']"/>
      </div>
      <div class="col-md-4"
           [ngClass]="{'has-error':!userForm.controls['roleId'].valid && userForm.controls['roleId'].touched}">
        <label>Role *</label>
        <select class="form-control form-control-sm"
                [ngModel]="roleId"
                [formControl]="userForm.controls['roleId']">
          <option value="">Select A Role</option>
          <option *ngFor="let r of roles" [value]="r.id">{{r.value}}</option>
        </select>
      </div>
    </div>
    <div class="row form-group">
      <div class="col-md-12">
        <label>Company Name</label>
        <input class="form-control form-control-sm"
               [ngModel]="companyName"
               [formControl]="userForm.controls['companyName']"/>
      </div>
    </div>
    <div class="row form-group">
      <div class="col-md-6">
        <label>Phone</label>
        <input class="form-control form-control-sm"
               [ngModel]="phone"
               [formControl]="userForm.controls['phone']"/>
      </div>
      <div class="col-md-6">
        <label>Mobile Phone</label>
        <input class="form-control form-control-sm"
               [ngModel]="phone2"
               [formControl]="userForm.controls['phone2']"/>
      </div>
    </div>
    <div class="row form-group">
      <div class="col-md-12">
        <label>Address</label>
        <input class="form-control form-control-sm"
               [ngModel]="address"
               [formControl]="userForm.controls['address']"/>
        <input class="form-control form-control-sm"
               [ngModel]="address2"
               [formControl]="userForm.controls['address2']"/>
      </div>
    </div>
    <div class="row form-group">
      <div class="col-md-5">
        <label>City</label>
        <input class="form-control form-control-sm"
               [ngModel]="city"
               [formControl]="userForm.controls['city']"/>
      </div>
      <div class="col-md-4">
        <label>State/Province/Region</label>
        <input class="form-control form-control-sm"
                [ngModel]="state"
                [formControl]="userForm.controls['state']"/>
      </div>
      <div class="col-md-3">
        <label>Postal Code</label>
        <input class="form-control form-control-sm"
               [ngModel]="zipCode"
               [formControl]="userForm.controls['zipCode']"/>
      </div>
    </div>
    <div class="row form-group">
      <div class="col-md-6">
        <label>Country</label>
        <select class="form-control form-control-sm"
               [ngModel]="country"
               [formControl]="userForm.controls['country']">
          <option disabled [value]="''">Select a Country</option>
          <option *ngFor="let c of countries" [value]="c">{{c}}</option>
        </select>
      </div>
    </div>
    <div class="row form-group">
      <div class="col-md-12 text-right">
        <button class="btn btn-sm btn-outline-primary" [disabled]="working">
          <span *ngIf="working && user.email">Saving</span>
          <span *ngIf="!working || (working && !user.email)">Save</span>
        </button>
        <a *ngIf="isNewUser" routerLink="../" class="btn btn-sm btn-outline-danger">Cancel</a>
        <a *ngIf="!isNewUser" routerLink="../../" class="btn btn-sm btn-outline-danger">Cancel</a>
      </div>
    </div>
  </form>
  `,
  styles: []
})
export class UserEditorFormComponent {
  @Input() isNewUser: boolean;
  @Input() user: User;
  @Input() roles: KvItem[];
  @Input() working: boolean;
  @Input() usernameTaken: boolean;
  @Input() emailTaken: boolean;
  @Output() checkUsername: EventEmitter<string> = new EventEmitter();
  @Output() checkEmail: EventEmitter<string> = new EventEmitter();
  @Output() saveUser: EventEmitter<User> = new EventEmitter();

  public userForm: FormGroup;
  public countries: string[];

  validateUsername() {
    const un = this.userForm.controls['userName'].value;
    if (un) {
      this.checkUsername.emit(un);
    }
  }

  validateEmail() {
    const em = this.userForm.controls['email'].value;
    if (em) {
      this.checkEmail.emit(em);
    }
  }

  get email () {
    return this.user.email;
  }

  get firstName () {
    return this.user.firstName;
  }

  get lastName () {
    return this.user.lastName;
  }

  get address () {
    return this.user.address;
  }

  get address2 () {
    return this.user.address2;
  }

  get companyName () {
    return this.user.companyName;
  }

  get phone () {
    return this.user.phone;
  }

  get phone2 () {
    return this.user.phone2;
  }

  get city () {
    return this.user.city;
  }

  get state () {
    return this.user.state;
  }

  get zipCode () {
    return this.user.zipCode;
  }

  get enabled () {
    return this.user.enabled;
  }

  get roleId () {
    return this.user.roleId;
  }

  get country () {
    if (!this.user || !this.user.country) { return ''; }
    return this.user.country;
  }

  get username() {
    if (this.user.userName) {
      this.userForm.controls['userName'].disable();
    }
    return this.user.userName;
  }

  constructor(fb: FormBuilder,
              countryService: CountryService) {
    this.userForm = fb.group({
      'email': [null, Validators.required],
      'firstName': [null, Validators.required],
      'lastName': [null, Validators.required],
      'roleId': [null, Validators.required],
      'companyName': [null],
      'phone': [null],
      'phone2': [null],
      'address': [null],
      'address2': [null],
      'city': [null],
      'state': [null],
      'zipCode': [null],
      'enabled': [null],
      'userName': [null],
      'country': [null]
    });
    this.countries = countryService.getCountryNames();
  }

  save() {
    if (!this.userForm.valid && !this.emailTaken && !this.usernameTaken) {
      this.userForm.controls['email'].markAsTouched();
      this.userForm.controls['firstName'].markAsTouched();
      this.userForm.controls['lastName'].markAsTouched();
      this.userForm.controls['roleId'].markAsTouched();
      return;
    }
    const newUser = this.userForm.value as User;
    if (!newUser.userName) {
      newUser.userName = this.user.email;
    }
    this.saveUser.emit(newUser);
  }
}



// WEBPACK FOOTER //
// C:/Users/jstan/Projects/usda-document-client/src/app/modules/core/components/user-editor-form.ts